import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";

export default function AuthForm({ redirectTo = "/book" }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const next = params.get("next") || redirectTo;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await register(name, email, password);
        toast.success("Account created — welcome!");
      } else {
        await login(email, password);
        toast.success("Logged in");
      }
      navigate(next);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-2 border-black bg-white shadow-hard-lg p-8" data-testid="auth-form">
      <div className="flex border-2 border-black mb-8">
        <button onClick={() => setMode("signup")} data-testid="auth-mode-signup"
          className={`flex-1 py-3 font-display font-bold uppercase tracking-wider text-sm ${mode === "signup" ? "bg-black text-white" : "bg-white"}`}>
          Create account
        </button>
        <button onClick={() => setMode("login")} data-testid="auth-mode-login"
          className={`flex-1 py-3 font-display font-bold uppercase tracking-wider text-sm border-l-2 border-black ${mode === "login" ? "bg-black text-white" : "bg-white"}`}>
          Log in
        </button>
      </div>
      <form onSubmit={submit} className="space-y-5">
        {mode === "signup" && (
          <div>
            <label className="eyebrow block mb-2" htmlFor="auth-name">Your name</label>
            <input id="auth-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name-input"
              className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" placeholder="Jane Appleton" />
          </div>
        )}
        <div>
          <label className="eyebrow block mb-2" htmlFor="auth-email">Email</label>
          <input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input"
            className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" placeholder="you@example.com" />
        </div>
        <div>
          <label className="eyebrow block mb-2" htmlFor="auth-password">Password</label>
          <input id="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "signup" ? 8 : 1} data-testid="auth-password-input"
            className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" placeholder={mode === "signup" ? "At least 8 characters" : "Your password"} />
        </div>
        <button type="submit" disabled={busy} data-testid="auth-submit-btn"
          className="w-full border-2 border-black bg-accent py-4 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
          {busy ? "One moment…" : mode === "signup" ? "Create account & continue" : "Log in & continue"}
        </button>
        {mode === "login" && (
          <p className="text-center">
            <Link to="/forgot-password" className="text-sm underline text-neutral-500 hover:text-black" data-testid="auth-forgot-link">
              Forgot your password?
            </Link>
          </p>
        )}
      </form>
      <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
        Your details are only used to manage your repairs and offers. Questions? <Link to="/about" className="underline">About us</Link>.
      </p>
    </div>
  );
}
